import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useCompanyFlow } from "@/hooks/useCompanyFlow";
import { Table } from "react-bootstrap";
import { Skeleton, Pagination } from "antd";
import { useIsMutating } from "@tanstack/react-query";
import * as _ from "lodash";
import { useGetOrdersByUser } from "@/hooks/useOrders";
import { useAuth } from "@/_zustand/auth";
import { useMachine } from "@/_zustand/machine";
import { formatMoney } from "@/lib";
import { sortOrdersByRecency } from "@/lib/ordersSort";
import { estaVencido, textoVigenciaQr } from "@/lib/orderExpiry";
import WhatsappGreen from "@/assets/imgs/whatsappGreen.png";
import SaveIcon from "@/assets/imgs/saveIcon.png";
import "./myorders.css";
import { ShareOrderModal } from "./ShareOrderModal";
import { OrderDetailModal } from "./OrderDetailModal";
import Empty from "@/assets/imgs/empty_orders.png";

export const MyOrdersCompanyPage = () => {
  const { user } = useAuth();
  const { empresa_id } = useMachine();
  const navigate = useNavigate();
  const { companyHomePath } = useCompanyFlow();
  const isMutating = useIsMutating({ mutationKey: ["useGetOrdersByUser"] });
  const { mutateAsync: getOrdersMutateAsync } = useGetOrdersByUser();
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showModalQr, setShowModalQr] = useState(false);
  const [showModalOrder, setShowModalOrder] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);

  useEffect(() => {
    getOrdersMutateAsync(user.usuario_id)
      .then((resp) => {
        setOrders(Array.isArray(resp) ? resp : []);
      })
      .catch((error) => {
        console.log("error getOrders: ", error);
      });
  }, [getOrdersMutateAsync, user.usuario_id]);

  // ⭐️ Nueva función auxiliar para generar el enlace
  const generateWhatsappLink = (orderData) => {
    // 1. Construir el mensaje con los datos que tenemos *ahora* (orderData)
    const specificMessage = `Hola, Soy ${user.usuario_nombre} ${user.usuario_apellido}. Necesito ayuda con mi pedido ${orderData.pedido_id}`;

    const genericMessage = `Hola, ¡Espero que estés bien! Soy ${user.usuario_nombre} ${user.usuario_apellido}. Necesito ayuda con mi pedido`;

    // Usamos el mensaje específico si orderData existe, si no, usamos el genérico
    const message = orderData ? specificMessage : genericMessage;

    const encodedMessage = encodeURIComponent(message);

    // Determinar el número de teléfono
    const phoneNumber =
      orderData && orderData.maquina_telefono
        ? orderData.maquina_telefono
        : "56912345678";

    return `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
  };

  const handleShowQr = (order, event) => {
    event.stopPropagation();
    if (estaVencido(order.pedido_vigente)) return;
    setSelectedOrder(order);
    setShowModalQr(true);
  };

  // Filter orders by company
  const filteredOrders = useMemo(() => {
    if (!orders) return [];

    // Strict filtering: If no company context is found, show NO orders rather than ALL orders.
    if (!empresa_id) {
      console.warn(
        "No empresa_id found in useMachine store. Showing empty list.",
      );
      return [];
    }

    const ordersForCompany = orders.filter((order) => {
      // console.log("Checking Order:", order.pedido_id, "Empresa:", order.empresa_id);
      return Number(order.empresa_id) === Number(empresa_id);
    });

    // Más reciente primero (defensa en profundidad, ver src/lib/ordersSort.js)
    return sortOrdersByRecency(ordersForCompany);
  }, [orders, empresa_id]);

  // Lógica para calcular los elementos a mostrar en la página actual
  const currentOrders = useMemo(() => {
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const slicedOrders = filteredOrders.slice(
      indexOfFirstItem,
      indexOfLastItem,
    );
    return slicedOrders;
  }, [filteredOrders, currentPage, itemsPerPage]);

  const handlePageChange = (page, pageSize) => {
    setCurrentPage(page);
    setItemsPerPage(pageSize); // Esto se activa si el Pagination permite cambiar el tamaño de página
  };

  const handleShowDetails = (order) => {
    setSelectedOrder(order);
    setShowModalOrder(true);
  };

  const goToHome = () => {
    navigate(companyHomePath);
  };

  return (
    <section id="pago" className="container my-5" style={{ flexGrow: 1 }}>
      {/* <QrModal show={showModalQr} order={selectedOrder} onHide={() => setShowModalQr(false)} /> */}
      <ShareOrderModal
        show={showModalQr}
        order={selectedOrder}
        onHide={() => setShowModalQr(false)}
      />
      <OrderDetailModal
        show={showModalOrder}
        order={selectedOrder}
        onHide={() => setShowModalOrder(false)}
      />
      {/* <OrderModal show={showModalOrder} order={selectedOrder} onHide={() => setShowModalOrder(false)} /> */}
      <div className="row">
        <div className="col-12 col-sm-6 col-md-6 col-lg-6 mb-3 inter-regular">
          <h2 className="inter-bold">Mis Pedidos</h2>
        </div>
      </div>
      <div className="row">
        {_.size(filteredOrders) > 0 ? (
          <div className="col-12 col-sm-6 col-md-6 col-lg-12 mb-3 inter-regular">
            <Skeleton loading={isMutating} active>
              <div className="tabla-container">
                <div className="tabla-compras">
                  {/* Ant Design Table es muy bueno para esto, pero si usas React-Bootstrap, manténlo */}
                  {/* Si usas Antd Table, la estructura cambia y el componente Pagination se integra mejor */}
                  <Table hover responsive>
                    {" "}
                    {/* Si es React-Bootstrap Table */}
                    <thead>
                      <tr>
                        <th>Tienda</th>
                        <th>Estado</th>
                        <th>Artículos</th>
                        <th>Monto</th>
                        <th>Fecha</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentOrders.map((order) => (
                        <tr
                          key={order.pedido_id}
                          onClick={() => handleShowDetails(order)}
                        >
                          {/* Columna "Tienda": el nombre de la tienda, no su
                              direccion. `maquina_nombre` viene del backend
                              (pedidosByEmpresa/usuario/[idUsuario].get.ts:24,
                              que ya resuelve el nombre del grupo cuando el
                              pedido es de un grupo de maquinas); la direccion
                              queda solo como respaldo para pedidos antiguos
                              donde el nombre pueda venir vacio. */}
                          <td>
                            {order.maquina_nombre || order.maquina_direccion}
                          </td>
                          <td>
                            <span className="estado rojo">
                              {order.pedido_estado}
                            </span>
                          </td>
                          <td>{order.pedido_nro_articulos}</td>
                          <td>{formatMoney(order.pedido_precio_total)}</td>
                          <td>
                            <div className="fecha">
                              <span>{order.pedido_fecha}</span>
                              <small>
                                {textoVigenciaQr(order.pedido_vigente)}
                              </small>
                            </div>
                          </td>
                          <td className="acciones">
                            <button
                              className="accion-descargar"
                              onClick={(e) => handleShowQr(order, e)}
                              disabled={estaVencido(order.pedido_vigente)}
                              title={
                                estaVencido(order.pedido_vigente)
                                  ? "El QR de este pedido ya venció"
                                  : undefined
                              }
                            >
                              <img src={SaveIcon} alt="carrito" />
                            </button>
                            <button
                              className="accion-whatsapp"
                              onClick={() => {
                                // 1. Opcional: Establece el estado si es necesario para el resto del componente.
                                setSelectedOrder(order);

                                // 2. 🔑 CLAVE: Genera el enlace usando la variable 'order' actual.
                                const dynamicWhatsappLink =
                                  generateWhatsappLink(order);

                                // 3. Abre la ventana con el enlace recién creado.
                                window.open(dynamicWhatsappLink, "_blank");
                              }}
                            >
                              <img src={WhatsappGreen} alt="carrito" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </div>
            </Skeleton>

            {/* --- Componente de Paginación de Ant Design --- */}
            <div
              style={{
                display: "block",
                margin: "0 auto",
                marginTop: "20px",
                width: "fit-content",
              }}
            >
              <Pagination
                current={currentPage}
                pageSize={itemsPerPage}
                total={filteredOrders.length}
                onChange={handlePageChange}
                showSizeChanger
                pageSizeOptions={["5", "10", "20", "50"]}
              />
            </div>
            {/* --- Fin Componente de Paginación --- */}
          </div>
        ) : (
          <div
            className="col-12 col-sm-12 col-md-12 col-lg-12 mb-3 text-center"
            style={{ padding: "12rem" }}
          >
            <img
              src={Empty}
              alt={"no hay ordenes"}
              className="order-detail-item-img"
            />
            <p>No tienes pedidos aun</p>
            <button className="orders-empty-button" onClick={goToHome}>
              Ir a tiendas
            </button>
          </div>
        )}
      </div>
    </section>
  );
};
